/**
 * Runtime configuration, read from `/config.json` at start-up.
 *
 * Tenant and client id come from this file and never from a build-time
 * constant, so one built artefact ships to every environment (W-7.7). The file
 * is fetched before sign-in, which is also why no permission can gate it —
 * see shared-contract.md §2 on why `config.read` was removed.
 */

export interface EntraConfig {
  readonly tenantId: string;
  readonly clientId: string;
  readonly authority: string;
  readonly note?: string;
}

export interface RuntimeConfig {
  readonly facilityName: string;
  readonly facilitySubtitle: string;
  /** The long form, used on the sign-in panel. */
  readonly facilityLongName: string;
  readonly tenantName: string;
  /** Shown on the sign-in panel before any API call has been made. */
  readonly bayCount: number;
  readonly apiBaseUrl: string;
  readonly facilityTimeZone: string;
  /**
   * `Auth:LocalSignInEnabled`. **Must be false at handover** — that is an
   * acceptance criterion, not a suggestion (W-7.4).
   */
  readonly authLocalSignInEnabled: boolean;
  readonly entra: EntraConfig;
}

/** Used until `/config.json` resolves, and in tests. */
export const DEFAULT_RUNTIME_CONFIG: RuntimeConfig = {
  facilityName: 'Gewan Island',
  facilitySubtitle: 'MUB basement',
  facilityLongName: 'Mixed Use Building basement',
  tenantName: 'United Development Company',
  bayCount: 362,
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? '/api/v1',
  facilityTimeZone: 'Asia/Qatar',
  authLocalSignInEnabled: false,
  entra: {
    // OPEN-W3 — the real tenant, client id and group→role map come from UDC IT.
    tenantId: import.meta.env.VITE_TENANT_ID ?? '00000000-0000-0000-0000-000000000000',
    clientId: '00000000-0000-0000-0000-000000000000',
    authority: 'https://login.microsoftonline.com/common',
  },
};

let current: RuntimeConfig = DEFAULT_RUNTIME_CONFIG;
let loaded: Promise<RuntimeConfig> | null = null;

/** The config as last loaded. Synchronous, for code that cannot await. */
export function getRuntimeConfig(): RuntimeConfig {
  return current;
}

/** Replace the config — used by tests and by the mock's fault-injection panel. */
export function setRuntimeConfig(config: Partial<RuntimeConfig>): RuntimeConfig {
  current = { ...current, ...config, entra: { ...current.entra, ...config.entra } };
  return current;
}

export async function loadRuntimeConfig(url = '/config.json'): Promise<RuntimeConfig> {
  if (loaded) return loaded;
  loaded = (async () => {
    try {
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error(`config.json responded ${response.status}`);
      const body = (await response.json()) as Partial<RuntimeConfig>;
      return setRuntimeConfig(body);
    } catch {
      // A missing or malformed config.json must not white-screen the app: the
      // defaults keep it bootable, and local sign-in defaults to OFF so the
      // failure mode is the safe one.
      return setRuntimeConfig({ authLocalSignInEnabled: false });
    }
  })();
  return loaded;
}

/** Test seam — forget the memoised load. */
export function resetRuntimeConfig(): void {
  current = DEFAULT_RUNTIME_CONFIG;
  loaded = null;
}
